import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import {
  entregas as api,
  type BuscaDeEntregas,
  type EntregaNaLista,
  type LeituraDaFoto,
  type Retencao,
} from '../api/entregas'
import { ErroApi } from '../api/client'
import { Aviso } from '../componentes/Aviso'
import { Botao } from '../componentes/Botao'
import { Campo } from '../componentes/Campo'
import { Cartao } from '../componentes/Cartao'
import { Estado } from '../componentes/Estado'
import { useSessao } from '../auth/useSessao'
import { mensagemDeErro } from './mensagemDeErro'
import { marcaDaRota } from './marcaDaRota'
import './usuarios.css'
import './entregas.css'

const VAZIO = { referencia: '', cliente: '', entregueEm: '', recebidoPor: '', observacoes: '' }

/** Os campos que a leitura da foto pode sugerir. As observações ficam de fora. */
type CampoSugerivel = 'referencia' | 'cliente' | 'entregueEm' | 'recebidoPor'

const NENHUM: ReadonlySet<CampoSugerivel> = new Set()

/**
 * A sugestão da leitura no formato de cada campo do formulário.
 *
 * ⚠️ **A hora não é lida da foto.** O campo é de data e hora, então a data entra ao
 * meio-dia — e a marca do campo diz isso, para ninguém tomar a hora por lida.
 *
 * 🔴 **Quando a data lida é HOJE, entra a hora de agora** (revisão do #79): meio-dia, lido
 * às dez da manhã, seria uma entrega registrada no futuro.
 */
function valoresDaLeitura(lido: LeituraDaFoto): Record<CampoSugerivel, string | null> {
  const agora = paraCampoDeData(new Date().toISOString())
  const data = lido.dataDoRecebimento

  return {
    referencia: lido.referencia,
    cliente: lido.cliente,
    recebidoPor: lido.recebidoPor,
    entregueEm: data ? (agora.startsWith(`${data}T`) ? agora : `${data}T12:00`) : null,
  }
}

/**
 * ⚠️ **O contato não é editável aqui** (revisão do PR #68, F2): o campo aceitava o número
 * do contato sem mostrar quem é, e um dígito trocado ligaria o canhoto — com a assinatura
 * de quem recebeu — a outro cliente. A edição devolve o vínculo que o registro já tem.
 */
type FormDeEdicao = typeof VAZIO & { contatoId: number | null }

/**
 * O instante gravado, no formato do campo `datetime-local` — **na hora local**.
 *
 * ⚠️ `toISOString().slice(0, 16)` seria a hora UTC: o campo mostraria três horas
 * a mais e, salvo sem mexer, o registro andaria três horas a cada edição.
 */
function paraCampoDeData(iso: string) {
  const d = new Date(iso)
  const dois = (n: number) => String(n).padStart(2, '0')

  return `${d.getFullYear()}-${dois(d.getMonth() + 1)}-${dois(d.getDate())}T${dois(d.getHours())}:${dois(d.getMinutes())}`
}

/**
 * Registro de entrega com comprovante (demanda 043 · `P-27`).
 *
 * 🔴 **É o marco do Mês 4, e ele não vem de transportadora.** Nem os Correios
 * nem a Braspress devolvem canhoto por API — quem alimenta é a expedição, por
 * esta tela. Antes dela, os 1.392 comprovantes do histórico circulavam por
 * WhatsApp.
 *
 * Duas coisas que a tela diz em voz alta, porque calar sairia caro:
 *
 * - **busca sem resultado ≠ nenhuma entrega registrada**, e referência curta
 *   demais é um filtro *recusado*, não uma entrega que não existe;
 * - **o prazo de retenção fica à vista de quem registra** — o comprovante tem
 *   assinatura de alguém, e quem guarda precisa saber por quanto tempo.
 */
export function Entregas() {
  const { t } = useTranslation()
  const { pode } = useSessao()

  const marca = marcaDaRota() ?? ''
  const podeEditar = pode('EntregaGerenciar')

  const [busca, setBusca] = useState<BuscaDeEntregas | null>(null)
  const [procurado, setProcurado] = useState('')
  const [termo, setTermo] = useState('')
  const [falhou, setFalhou] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [salvando, setSalvando] = useState(false)
  const [confirmando, setConfirmando] = useState<number | null>(null)

  const [form, setForm] = useState(VAZIO)
  const arquivos = useRef<HTMLInputElement>(null)

  // A leitura da foto SUGERE: preenche só o que está vazio, marca o que preencheu e não
  // grava nada. A marca sai quando a pessoa mexe no campo ou registra a entrega.
  const [temFoto, setTemFoto] = useState(false)
  const [lendo, setLendo] = useState(false)
  const [sugeridos, setSugeridos] = useState<ReadonlySet<CampoSugerivel>>(NENHUM)
  const [resultadoDaLeitura, setResultadoDaLeitura] = useState<
    { tipo: LeituraDaFoto['tipoDeDocumento']; campos: number } | null
  >(null)
  const [erroDaLeitura, setErroDaLeitura] = useState<string | null>(null)

  // O que está na tela AGORA, para a resposta da leitura não decidir pelo que havia quando
  // o botão foi apertado: a pessoa pode digitar enquanto a foto é lida.
  const formAtual = useRef(form)
  formAtual.current = form

  // 🔴 A leitura que está valendo (revisão do #79). Registrar a entrega ou trocar a foto
  // durante a leitura invalida a resposta que ainda vai chegar: sem isto ela preencheria o
  // formulário já limpo — o da PRÓXIMA entrega — com os dados da foto anterior.
  const leituraValida = useRef(0)

  function invalidarLeitura() {
    leituraValida.current++
    setLendo(false)
  }

  // A correção do registro (conversa 358, 21/09). O PUT e o `CorrigirAsync` existiam
  // desde a 043, e nenhum botão os chamava: a referência gravada como "Teste 1 - 64959"
  // só saía excluindo e registrando de novo — e a auditoria perdia o "antes".
  const [editando, setEditando] = useState<number | null>(null)
  const [edicao, setEdicao] = useState<FormDeEdicao>({ ...VAZIO, contatoId: null })

  const [retencao, setRetencao] = useState<Retencao | null>(null)
  const [dias, setDias] = useState('')

  const carregar = useCallback(
    async (referencia = '') => {
      setFalhou(false)
      try {
        setBusca(await api.listar(marca, referencia ? { referencia } : undefined))
        setProcurado(referencia)
      } catch {
        setFalhou(true)
      }
    },
    [marca],
  )

  useEffect(() => {
    void carregar()

    api
      .retencao()
      .then((r) => {
        setRetencao(r)
        setDias(String(r.dias))
      })
      .catch(() => setRetencao(null))
  }, [carregar])

  // 🔴 A mesma regra do servidor, dita ANTES de o arquivo subir.
  //
  // O back recusa e explica — mas só depois de o navegador ter enviado a foto
  // inteira, e a mensagem chega como erro de servidor no meio da tela. Validar
  // aqui é sobre o momento, não sobre a segurança: quem barra continua sendo o
  // servidor.
  const curta = (referencia: string) =>
    referencia.trim().length > 0 &&
    referencia.replace(/[^a-zA-Z0-9]/g, '').length < (busca?.minimo ?? 5)

  const referenciaCurta = curta(form.referencia)
  const edicaoCurta = curta(edicao.referencia)

  function abrirEdicao(entrega: EntregaNaLista) {
    setConfirmando(null)
    setErro(null)
    setEditando(entrega.id)
    setEdicao({
      referencia: entrega.referencia,
      cliente: entrega.cliente,
      entregueEm: paraCampoDeData(entrega.entregueEm),
      recebidoPor: entrega.recebidoPor ?? '',
      observacoes: entrega.observacoes ?? '',
      contatoId: entrega.contatoId ?? null,
    })
  }

  /**
   * O que impede gravar — ou nulo. A mesma conferência no cadastro e na edição.
   *
   * 🔴 **Referência repetida não se grava** (revisão do PR #68, F1). O índice de
   * `(marca, referência)` não é único, e dois registros com a mesma referência deixam a
   * busca AMBÍGUA: o Copiloto responde *"existe mais de um registro"* e não posta nada.
   * Em 21/09 isso estava a um clique: o registro 1 era `Teste 1 - 64959`, o 2 era `64959`,
   * e a dica nova convida a corrigir o 1 para `64959`.
   *
   * ⚠️ **A data vazia também para aqui** (F4): `new Date('')` lança ao virar ISO, e o erro
   * chegava à tela como falha de rede.
   *
   * ⚠️ **E a referência em branco** (2ª revisão do #68): sem ela, a listagem sai SEM filtro,
   * devolve todos os registros, e a tela acusava "já existe o registro #2" para um campo vazio.
   */
  async function impedimento(referencia: string, entregueEm: string, proprio: number | null) {
    if (!referencia.trim()) return t('entregas.referenciaObrigatoria')
    if (!entregueEm) return t('entregas.dataObrigatoria')

    const mesma = await api.listar(marca, { referencia: referencia.trim() })
    const outro = mesma.itens.find((e) => e.id !== proprio)

    return outro ? t('entregas.referenciaRepetida', { id: outro.id, referencia: outro.referencia }) : null
  }

  async function salvarEdicao(evento: FormEvent) {
    evento.preventDefault()
    if (editando === null) return

    if (edicaoCurta) {
      setErro(t('entregas.referenciaCurtaCadastro', { minimo: busca?.minimo ?? 5 }))
      return
    }

    setSalvando(true)
    setErro(null)

    try {
      const motivo = await impedimento(edicao.referencia, edicao.entregueEm, editando)
      if (motivo) {
        setErro(motivo)
        return
      }

      // O contato vai SEMPRE, e é o que o registro já tinha: o PUT grava o que recebe, e
      // omitir desvincularia em silêncio.
      await api.salvar(marca, editando, edicao)
      setEditando(null)
      await carregar(procurado)
    } catch (falha) {
      setErro(mensagemDeErro(falha, t))
    } finally {
      setSalvando(false)
    }
  }

  /** A pessoa mexeu: o valor passa a ser dela, e a marca de sugestão sai. */
  function digitar(campo: keyof typeof VAZIO, valor: string) {
    setForm((atual) => ({ ...atual, [campo]: valor }))

    setSugeridos((atuais) => {
      if (!atuais.has(campo as CampoSugerivel)) return atuais

      const resto = new Set(atuais)
      resto.delete(campo as CampoSugerivel)

      return resto
    })
  }

  async function lerFoto() {
    const foto = arquivos.current?.files?.[0]
    if (!foto) return

    const esta = ++leituraValida.current

    setLendo(true)
    setErroDaLeitura(null)
    setResultadoDaLeitura(null)

    try {
      const lido = await api.lerFoto(marca, foto)

      if (esta !== leituraValida.current) return

      const valores = valoresDaLeitura(lido)

      // 🔴 Só campo VAZIO recebe sugestão. O que a pessoa digitou — antes ou durante a
      // leitura — vale mais que o que o modelo leu.
      const naTela = formAtual.current
      const novos = (Object.keys(valores) as CampoSugerivel[]).filter(
        (campo) => valores[campo] !== null && !naTela[campo].trim(),
      )

      if (novos.length > 0) {
        setForm((atual) => {
          const proximo = { ...atual }

          for (const campo of novos) if (!atual[campo].trim()) proximo[campo] = valores[campo] as string

          return proximo
        })

        setSugeridos((atuais) => new Set([...atuais, ...novos]))
      }

      setResultadoDaLeitura({ tipo: lido.tipoDeDocumento, campos: novos.length })
    } catch (falha) {
      if (esta !== leituraValida.current) return

      // A recusa do arquivo (400) vem com a frase do servidor; o resto é a leitura que não
      // aconteceu, e a tela segue utilizável à mão.
      const detalhe =
        falha instanceof ErroApi && falha.status === 400
          ? (falha.corpo as { detail?: string } | null)?.detail
          : undefined

      setErroDaLeitura(detalhe ?? t('entregas.leituraFalhou'))
    } finally {
      // A leitura invalidada já devolveu o botão; não mexe no estado de quem veio depois.
      if (esta === leituraValida.current) setLendo(false)
    }
  }

  /** Classe e texto da marca de sugestão — a cor não avisa sozinha (UI-09). */
  const marcaDeSugestao = (campo: CampoSugerivel, dica?: string) => {
    if (!sugeridos.has(campo)) return { ajuda: dica }

    const aviso = t(campo === 'entregueEm' ? 'entregas.sugeridoData' : 'entregas.sugerido')

    return {
      className: 'campo__entrada entregas__sugerido',
      'data-sugerido': 'true',
      ajuda: dica ? `${aviso} ${dica}` : aviso,
    }
  }

  async function registrar(evento: FormEvent) {
    evento.preventDefault()

    const fotos = arquivos.current?.files
    if (!fotos || fotos.length === 0) return

    if (referenciaCurta) {
      setErro(t('entregas.referenciaCurtaCadastro', { minimo: busca?.minimo ?? 5 }))
      return
    }

    setSalvando(true)
    setErro(null)

    try {
      const motivo = await impedimento(form.referencia, form.entregueEm, null)
      if (motivo) {
        setErro(motivo)
        return
      }

      await api.registrar(marca, { ...form, fotos })
      setForm(VAZIO)
      invalidarLeitura()
      setSugeridos(NENHUM)
      setResultadoDaLeitura(null)
      setErroDaLeitura(null)
      setTemFoto(false)
      if (arquivos.current) arquivos.current.value = ''
      await carregar()
    } catch (falha) {
      setErro(mensagemDeErro(falha, t))
    } finally {
      setSalvando(false)
    }
  }

  async function excluir(entrega: EntregaNaLista) {
    // Duas etapas em vez de `confirm()`: diálogo do navegador trava a página e
    // não é estilizável nem traduzível.
    if (confirmando !== entrega.id) {
      setConfirmando(entrega.id)
      return
    }

    setConfirmando(null)
    setErro(null)

    try {
      await api.excluir(marca, entrega.id)
      await carregar(procurado)
    } catch (falha) {
      setErro(mensagemDeErro(falha, t))
    }
  }

  async function guardarRetencao(evento: FormEvent) {
    evento.preventDefault()
    setErro(null)

    try {
      const salva = await api.salvarRetencao(Number(dias))
      setRetencao((atual) => (atual ? { ...atual, ...salva } : atual))
    } catch (falha) {
      setErro(mensagemDeErro(falha, t))
    }
  }

  if (falhou)
    return <Estado tipo="erro" acao={<Botao onClick={() => carregar()}>{t('comum.tentarDeNovo')}</Botao>} />

  if (!busca) return <Estado tipo="carregando" />

  const nada = busca.itens.length === 0

  return (
    <section className="usuarios">
      <header className="usuarios__cabecalho">
        <div>
          <h1 className="usuarios__titulo">{t('entregas.titulo')}</h1>
          <p className="usuarios__descricao">{t('entregas.descricao')}</p>
        </div>
      </header>

      {erro && <Aviso tom="perigo">{erro}</Aviso>}

      <Cartao titulo={t('entregas.procurar')}>
        <form
          className="entregas__busca"
          onSubmit={(e) => {
            e.preventDefault()
            void carregar(termo.trim())
          }}
        >
          <Campo
            rotulo={t('entregas.referencia')}
            ajuda={t('entregas.referenciaDica', { minimo: busca.minimo })}
            value={termo}
            onChange={(e) => setTermo(e.target.value)}
          />

          <div className="entregas__acoes">
            <Botao type="submit">{t('entregas.procurar')}</Botao>

            {procurado && (
              <Botao
                variante="secundario"
                onClick={() => {
                  setTermo('')
                  void carregar()
                }}
              >
                {t('entregas.limparBusca')}
              </Botao>
            )}
          </div>
        </form>

        {/* 🔴 Filtro recusado ≠ entrega inexistente. Sem esta linha, quem digita
            quatro dígitos conclui que a entrega não foi registrada. */}
        {busca.referenciaCurta && (
          <Aviso tom="alerta">{t('entregas.referenciaCurta', { minimo: busca.minimo })}</Aviso>
        )}
      </Cartao>

      {podeEditar && (
        <Cartao titulo={t('entregas.nova')}>
          <form className="entregas__form" onSubmit={registrar}>
            <Campo
              rotulo={t('entregas.referencia')}
              obrigatorio
              erro={referenciaCurta
                ? t('entregas.referenciaCurtaCadastro', { minimo: busca.minimo })
                : undefined}
              value={form.referencia}
              onChange={(e) => digitar('referencia', e.target.value)}
              {...marcaDeSugestao('referencia', t('entregas.referenciaCadastroDica'))}
            />

            <Campo
              rotulo={t('entregas.cliente')}
              obrigatorio
              value={form.cliente}
              onChange={(e) => digitar('cliente', e.target.value)}
              {...marcaDeSugestao('cliente')}
            />

            <Campo
              rotulo={t('entregas.entregueEm')}
              obrigatorio
              type="datetime-local"
              value={form.entregueEm}
              onChange={(e) => digitar('entregueEm', e.target.value)}
              {...marcaDeSugestao('entregueEm')}
            />

            <Campo
              rotulo={t('entregas.recebidoPor')}
              value={form.recebidoPor}
              onChange={(e) => digitar('recebidoPor', e.target.value)}
              {...marcaDeSugestao('recebidoPor')}
            />

            <label className="campo">
              <span className="campo__rotulo">{t('entregas.observacoes')}</span>
              <textarea
                className="campo__entrada"
                rows={2}
                value={form.observacoes}
                onChange={(e) => digitar('observacoes', e.target.value)}
              />
            </label>

            <label className="campo">
              <span className="campo__rotulo">
                {t('entregas.fotos')}
                <span className="campo__obrigatorio" aria-hidden="true">
                  *
                </span>
              </span>
              <input
                ref={arquivos}
                type="file"
                accept="image/*,application/pdf"
                multiple
                required
                onChange={(e) => {
                  invalidarLeitura()
                  setTemFoto((e.target.files?.length ?? 0) > 0)
                  setErroDaLeitura(null)
                  setResultadoDaLeitura(null)
                }}
              />
              <span className="entregas__nota">{t('entregas.fotosDica')}</span>
            </label>

            {/* Fora do <label> do arquivo: dentro dele, o clique no botão abriria o seletor. */}
            <div className="entregas__leitura">
              <div className="entregas__acoes">
                <Botao onClick={lerFoto} carregando={lendo} disabled={!temFoto || lendo}>
                  {lendo ? t('entregas.lendoFoto') : t('entregas.lerFoto')}
                </Botao>
              </div>

              <span className="entregas__nota">{t('entregas.lerFotoDica')}</span>

              {erroDaLeitura && <Aviso tom="alerta">{erroDaLeitura}</Aviso>}

              {resultadoDaLeitura && (
                <Aviso tom={resultadoDaLeitura.campos > 0 ? 'info' : 'alerta'}>
                  {t(`entregas.tipoLido.${resultadoDaLeitura.tipo}`)}{' '}
                  {resultadoDaLeitura.campos > 0
                    ? t('entregas.leituraFeita', { count: resultadoDaLeitura.campos })
                    : t('entregas.leituraSemCampos')}
                </Aviso>
              )}
            </div>

            <Botao type="submit" carregando={salvando}>
              {t('entregas.registrar')}
            </Botao>
          </form>
        </Cartao>
      )}

      <Cartao titulo={t('entregas.registradas')}>
        {nada ? (
          procurado ? (
            // Busca sem resultado é diferente de vazio: aqui existem entregas,
            // só não esta.
            <Estado
              tipo="vazio"
              titulo={t('entregas.semResultado', { referencia: procurado })}
              descricao={t('entregas.semResultadoDescricao')}
            />
          ) : (
            <Estado
              tipo="vazio"
              titulo={t('entregas.vazio')}
              descricao={t('entregas.vazioDescricao')}
            />
          )
        ) : (
          <ul className="entregas__lista">
            {busca.itens.map((e) => (
              <li key={e.id} className="entregas__item">
                <div className="entregas__fotos">
                  {e.fotos.map((f) =>
                    f.mime.startsWith('image/') ? (
                      <img
                        key={f.id}
                        className="entregas__previa"
                        src={api.foto(marca, e.id, f.id)}
                        alt={t('entregas.previaDe', { referencia: e.referencia })}
                      />
                    ) : (
                      <a
                        key={f.id}
                        className="entregas__previa entregas__previa--arquivo"
                        href={api.foto(marca, e.id, f.id)}
                      >
                        {f.nomeOriginal}
                      </a>
                    ),
                  )}
                </div>

                {editando === e.id ? (
                  <form
                    className="entregas__form entregas__dados"
                    aria-label={t('entregas.editarEntrega', { referencia: e.referencia })}
                    onSubmit={salvarEdicao}
                  >
                    <Campo
                      rotulo={t('entregas.referencia')}
                      obrigatorio
                      ajuda={t('entregas.referenciaCadastroDica')}
                      erro={edicaoCurta
                        ? t('entregas.referenciaCurtaCadastro', { minimo: busca.minimo })
                        : undefined}
                      value={edicao.referencia}
                      onChange={(ev) => setEdicao({ ...edicao, referencia: ev.target.value })}
                    />

                    <Campo
                      rotulo={t('entregas.cliente')}
                      obrigatorio
                      value={edicao.cliente}
                      onChange={(ev) => setEdicao({ ...edicao, cliente: ev.target.value })}
                    />

                    <Campo
                      rotulo={t('entregas.entregueEm')}
                      obrigatorio
                      type="datetime-local"
                      value={edicao.entregueEm}
                      onChange={(ev) => setEdicao({ ...edicao, entregueEm: ev.target.value })}
                    />

                    <Campo
                      rotulo={t('entregas.recebidoPor')}
                      value={edicao.recebidoPor}
                      onChange={(ev) => setEdicao({ ...edicao, recebidoPor: ev.target.value })}
                    />

                    <label className="campo">
                      <span className="campo__rotulo">{t('entregas.observacoes')}</span>
                      <textarea
                        className="campo__entrada"
                        rows={2}
                        value={edicao.observacoes}
                        onChange={(ev) => setEdicao({ ...edicao, observacoes: ev.target.value })}
                      />
                    </label>

                    <div className="entregas__acoes">
                      <Botao type="submit" variante="primario" carregando={salvando}>
                        {t('entregas.salvarEdicao')}
                      </Botao>
                      <Botao variante="secundario" onClick={() => setEditando(null)}>
                        {t('comum.cancelar')}
                      </Botao>
                    </div>
                  </form>
                ) : (
                <div className="entregas__dados">
                  <div className="entregas__linha">
                    <code className="entregas__referencia">{e.referencia}</code>
                    <span className="entregas__data">
                      {new Date(e.entregueEm).toLocaleString('pt-BR')}
                    </span>
                  </div>

                  <strong>{e.cliente}</strong>

                  {e.recebidoPor && (
                    <p className="entregas__campo">
                      {t('entregas.recebidoPor')}: {e.recebidoPor}
                    </p>
                  )}

                  {e.observacoes && <p className="entregas__campo">{e.observacoes}</p>}

                  <p className="entregas__autoria">
                    {t('entregas.registradoPor', {
                      quem: e.registradoPor,
                      quando: new Date(e.registradoEm).toLocaleDateString('pt-BR'),
                    })}
                  </p>

                  {podeEditar && (
                    <div className="entregas__acoes">
                      <Botao variante="secundario" onClick={() => abrirEdicao(e)}>
                        {t('entregas.editar')}
                      </Botao>
                      <Botao variante="secundario" onClick={() => excluir(e)}>
                        {confirmando === e.id ? t('entregas.confirmarExclusao') : t('comum.excluir')}
                      </Botao>
                    </div>
                  )}
                </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </Cartao>

      {retencao && (
        <Cartao titulo={t('entregas.retencao')}>
          <p className="entregas__nota">{t('entregas.retencaoExplicacao')}</p>

          {retencao.desligado && <Aviso tom="alerta">{t('entregas.retencaoDesligada')}</Aviso>}

          {podeEditar ? (
            <form className="entregas__busca" onSubmit={guardarRetencao}>
              <Campo
                rotulo={t('entregas.retencaoDias')}
                type="number"
                min={0}
                value={dias}
                ajuda={t('entregas.retencaoDica', { padrao: retencao.padrao })}
                onChange={(e) => setDias(e.target.value)}
              />

              <div className="entregas__acoes">
                <Botao type="submit">{t('comum.salvar')}</Botao>
              </div>
            </form>
          ) : (
            <p className="entregas__campo">{t('entregas.retencaoAtual', { dias: retencao.dias })}</p>
          )}
        </Cartao>
      )}
    </section>
  )
}
